import { useTranslation } from 'react-i18next';
import { useGet } from '@/query/fetchHooks';
import { useState, useContext, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, IconTile, TEOLoader, ProgressBar } from '@teo/components';
import {
  ArrowLeft,
  Blocks,
  TaskCheckmarkFilledNew,
  StartFlag,
  PersonInfo,
} from '@teo/components/icons';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { nthLastIndexOf, renderTemplate, nthIndexOf, useMobile } from '@/util';
import { NavContext } from '@/components/NavContext';
import { LearnContent } from '@/components/LearningPath/Learn/LearnContent';
import {
  sortDataLP,
  applyCompetenceSkips,
} from '@/components/LearningPath/util';
import { axios } from '@/axios';
import { config } from '@/config';
import { MilestonesContent } from '@/components/LearningPath/Learn/MilestonesContent';
import MobileSubPageMenu from '@/components/HeaderMobile/MobileSubPageMenu';

function MilestonesLanding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { auth } = useContext(AuthContext);
  const authUser: any = auth?.user;
  const { trainingId, lpgroupId } = useParams();
  const [training, setTraining] = useState<any>(null);
  const [mlsFollow, setMlsFollow] = useState<any>(null);
  const { setHeaderDisabled } = useContext(NavContext);

  const [lpGroup, setLpGroup] = useState<any>(null);
  const [isError, setIsError] = useState<any>(false);
  const [allNodes, setAllNodes] = useState<any>(null);
  const [allMilestones, setAllMilestones] = useState<any>(null);
  const [beforeMilestones, setBeforeMilestones] = useState<any>(null);
  const [nextMilestone, setNextMilestone] = useState<any>(null);
  const [isUserFollow, setIsUserFollow] = useState(true);
  const [loading, setLoading] = useState(true);
  const [allBeforeDone, setAllBeforeDone] = useState(false);

  const originPath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 2)
  );

  useGet(`/training/${trainingId}`, {
    onSuccess: async (res: any) => {
      setTraining(res?.data || {});
    },
  });

  useGet(`/trainingfollow/${trainingId}/${authUser?.id}`, {
    retry: false,
    onSuccess: async (res: any) => {
      setMlsFollow(res?.data || {});
      setIsUserFollow(true);
      setLoading(false);
    },
    onError: (res: any) => {
      setIsUserFollow(false);
      setLoading(false);
    },
  });

  useEffect(() => {
    setHeaderDisabled(true);
    return () => setHeaderDisabled(false);
  });

  useGet(`/lpgroupdetail/${lpgroupId}`, {
    retry: false,
    onSuccess: (result: any) => {
      setLpGroup(result?.data || {});
    },
    onError: (res: any) => {
      setIsError(true);
    },
  });

  function getNextById(array: any, id: any) {
    const index = array.findIndex((el: any) => el.objectId === id);
    if (index !== -1 && index < array.length - 1) {
      return array[index + 1];
    }
    return null;
  }

  function getAllBeforeById(array: any, id: any) {
    const index = array.findIndex(
      (el: any) => el.objectId === id || el.id === id
    );
    if (index !== -1) {
      return array.slice(0, index);
    }
    return [];
  }

  function getAllParentsByObjectId(items: any, targetObjectId: any) {
    const byId = new Map(items.map((item: any) => [item.id, item]));
    const start = items.find((item: any) => item.objectId === targetObjectId);

    if (!start) return [];

    const result: any = [];
    const visited = new Set();

    function walk(node: any) {
      if (!node?.roots?.length) return;

      for (const parentId of node.roots) {
        if (visited.has(parentId)) continue;
        visited.add(parentId);

        const parent = byId.get(parentId);
        if (parent) {
          result.push(parent);
          walk(parent);
        }
      }
    }

    walk(start);
    return result;
  }

  function markSelected(main: any, selected: any = [], extra: any = null) {
    const selectedIds = new Set([
      ...selected.map((el: any) => el?.id),
      ...(extra ? [extra?.id] : []),
    ]);

    return main.map((el: any) => ({
      ...el,
      isCompleted: selectedIds.has(el?.id),
    }));
  }

  function areAllPassed(arr: any) {
    return arr.every((item: any) => {
      if (item?.skippedByCompetences) return true;
      const hasProgress = item.progress === 1;
      const hasScore =
        item.maxScore === 0 && item.score === 0 && item?.isCorrected
          ? true
          : item.minScore > 0
          ? item.score / item.maxScore >= item?.minScore
          : true;
      return hasProgress && hasScore && item?.isCorrected;
    });
  }

  useGet(`/lpnodestatuses`, {
    enabled: !!lpGroup,
    lpgroupId: lpgroupId,
    userId: auth?.user?.id,
    onSuccess: async (res: any) => {
      const lpAllNodes = applyCompetenceSkips(
        res?.data?.filter((nds: any) => nds?.type !== 'root')
      );
      const lpContent = lpAllNodes?.filter(
        (nds: any) => nds?.learningpathId === training?.learningpathId
      );
      if (lpContent?.length > 0) {
        const sortData = sortDataLP(lpContent);
        const allLpMilestones = sortData?.filter(
          (nds: any) => nds?.subType === 'milestone'
        );
        const newNodedetails: any[] = [];

        for (const mls of allLpMilestones) {
          let milestonesDetail = null;

          try {
            milestonesDetail = await axios.get(
              `${config.backend}/training/${mls?.objectId}`
            );

            newNodedetails.push(milestonesDetail?.data);
          } catch (error) {
            console.error(error);
          }
        }
        setAllMilestones(
          markSelected(
            newNodedetails,
            getAllBeforeById(newNodedetails, training?.id),
            training
          )
        );
        setBeforeMilestones(getAllBeforeById(newNodedetails, training?.id));

        setNextMilestone(getNextById(allLpMilestones, training?.id));
        const allBefore = getAllParentsByObjectId(sortData, training?.id);
        const lpAllBefore: any = [];
        allBefore?.map((el: any) => {
          lpAllBefore?.push(el);
          if (el?.type === 'learningpath') {
            const thameContent = lpAllNodes?.filter(
              (nds: any) => el?.objectId === nds?.learningpathId
            );
            lpAllBefore?.push(...thameContent);
          }
        });
        setAllNodes(lpAllBefore);
        const allOk = areAllPassed(allBefore);
        setAllBeforeDone(allOk);
      }
    },
  });

  if (!training) return null;

  return (
    <div className="relative -mx-4 flex flex-col">
      {loading && (
        <div className="fixed inset-0 z-[201] m-auto flex min-h-screen min-w-full bg-white/75 p-10">
          <div className="m-auto">
            <TEOLoader
              loaderSrc="/images/TEOloader.gif"
              className="w-[110px]"
            />
          </div>
        </div>
      )}
      {auth && (
        <Button
          className="absolute top-5 left-5 z-[1] flex items-center gap-3 text-white"
          variant="text"
          onClick={() => {
            if (isUserFollow) {
              if (mlsFollow?.status === 'completed') {
                navigate(`${originPath}`);
              } else {
                if (allBeforeDone) {
                  const promises = [];
                  promises.push(
                    axios.put(
                      `${config.backend}/trainingfollow/${trainingId}/${authUser?.id}`,
                      {
                        progress: 1,
                        status: 'completed',
                        isCorrected: true,
                      }
                    )
                  );
                  Promise.allSettled(promises).then(() => {
                    navigate(`${originPath}`);
                  });
                } else {
                  navigate(`${originPath}`);
                }
              }
            } else {
              if (allBeforeDone) {
                const promises = [];
                promises.push(
                  axios.post(`${config.backend}/trainingfollows`, {
                    trainingId: trainingId,
                    userId: authUser?.id,
                    progress: 1,
                    status: 'completed',
                    isCorrected: true,
                  })
                );
                Promise.allSettled(promises).then(() => {
                  navigate(`${originPath}`);
                });
              } else {
                navigate(`${originPath}`);
              }
            }
          }}
        >
          <ArrowLeft className="mdw-5 h-6 w-6 md:h-5" />
          {!isMobile && <p>{t('pages.lp_landing.back_to_lpgroup')}</p>}
        </Button>
      )}
      <MilestonesContent
        authUser={authUser}
        training={training}
        trainingId={trainingId}
        allMilestones={allMilestones}
        beforeMilestones={beforeMilestones}
        nextMilestone={nextMilestone}
        allNodes={allNodes}
        mlsFollow={mlsFollow}
        isUserFollow={isUserFollow}
        allBeforeDone={allBeforeDone}
      />
    </div>
  );
}

export default MilestonesLanding;
